import {ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import {Subscription} from "rxjs";
import {distinctUntilChanged, filter, map} from "rxjs/operators";
import {get, isEmpty, pickBy} from 'lodash';

import {AgregationService} from "../../../../modules/aggregator/services";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {FormHandlerService} from "../../dynamic-form/services/form-handler.service";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {INavigationApplicationInterface} from "@ispm/navigation/interfaces";
import {IRevision} from "@ispm/test/interfaces/diagnostic-order.interface";
import {IWorkflow, IWorkflowItem} from "@ispm/core/interfaces/workflow.interface";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {InterpolateService} from "../../services/interpolate.service";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {Router} from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
  selector: 'gen-stop-workflow',
  templateUrl: './stop-workflow.component.html',
  styleUrls: ['./stop-workflow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StopWorkflowComponent implements OnInit, OnDestroy{
  @Input() currentWorkflow: IWorkflow;
  @Input() currentRevision: IRevision;
  @Input() latestRevisionId: string;
  @Input() tooltipText: string = 'Stop Workflow';
  @Input() tooltipPosition: string = 'BOTTOM';
  @Input() stopAction: any;
  @Input() activeJobItem: IWorkflowItem;
  @Input() cssClass: any;
  @Input() styleItem: any;
  @Input() isDisabled: boolean = false;

  public actionIconAvailable: boolean = false;

  private formId: string;
  private $subscription: Subscription = new Subscription();
  private needToSave: boolean = false;

  constructor(
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private formHandlerService : FormHandlerService,
    private aggregationService : AgregationService,
    private route: Router,
    private interpolateService: InterpolateService,
    private coreActionCreator: CoreActionCreatorService,
    private store: Store<IgenomicsState>,
    private routerManagerService: RouterManagerService
  ) {
  }

  ngOnInit(): void {
    this.$subscription.add(
      this.store.select('navigation').pipe(
        filter(data => !!data),
        distinctUntilChanged(),
        map(data => data.currentApp),
        filter(currentApp => !!currentApp),
      ).subscribe((currentApp: INavigationApplicationInterface) => {
        // get custom formId from test configuration or get stop form per application
        this.formId = get(this.stopAction, 'payload.formId', 'STOP-WORKFLOW-' + currentApp.id.toUpperCase())
        this.formHandlerService.getForm(this.formId);
        // just in case if we don't have custom form and application form use default form
        this.formHandlerService.getForm('STOP-WORKFLOW-DEFAULT');
      })
    );

    if (get(this.stopAction, 'icon')) {
      this.actionIconAvailable = true;
    }
  }

  stop(): void{
    this.$subscription.add(
      this.formHandlerService.getStoredFormById(this.formId).pipe(
        map(formData => formData)
      ).take(1).subscribe(formData => {
        if (!formData) {
          this.formId = 'STOP-WORKFLOW-DEFAULT';
        }

        let submitButtonText = get(this.stopAction, 'payload.submitButtonText')
          ? get(this.stopAction, 'payload.submitButtonText') : 'Stop Workflow';
        let cancelButtonText = get(this.stopAction, 'payload.cancelButtonText')
          ? get(this.stopAction, 'payload.cancelButtonText') : 'Cancel';
        const titleText = get(this.stopAction, 'payload.titleText')
          ? get(this.stopAction, 'payload.titleText') : 'Stop Workflow';

        if (!isEmpty(this.currentWorkflow) && (this.currentWorkflow.activeJob.toUpperCase() === 'NONE')) {
          submitButtonText = '';
        }
        else if (
          !isEmpty(this.activeJobItem) && (this.activeJobItem.status.toUpperCase() === 'COMPLETED'
          || this.activeJobItem.status.toUpperCase() === 'STOPPED')
        ) {
          submitButtonText = '';
        }

        this.createFormModal(this.formId, submitButtonText, cancelButtonText, titleText);
      })
    );
  }

  private createFormModal(currentFormId, approveButtonText, cancelButtonText, titleText) {
    this.needToSave = true;
    this.$subscription.add(
      this.formHandlerService.popupDynamicFormModal(
        titleText,
        currentFormId,
        {},
        approveButtonText,
        cancelButtonText
      ).pipe(distinctUntilChanged()).take(1).subscribe(data => {
        if (data['status'] === 1) {
          let formData = get(data, 'payload.formData');
          formData = pickBy(formData, (item) => !!item);
          // if in a property exists symbol new line need to shield this symbol for further work
          for (const property in formData) {
            if (typeof formData[property] === 'string') {
              formData[property] = formData[property].replace(/\n/g, '\\n');
            }
          }

          if (isEmpty(formData)) {
            this.notificationMessageService.error(this.notificationMessage.NO_MODIFIED_DATA_FOR_STOP_WORKFLOW);
          } else {
            this.coreActionCreator.setLoader(true);
            if(this.needToSave) {
              this.$subscription.add(
                this.aggregationService.saveFormData(currentFormId, formData, [], 'flatten', formData)
                  .subscribe(
                    savedData => {
                      if (savedData.code === 2) {
                        this.coreActionCreator.setLoader(false);
                        this.notificationMessageService.success(this.notificationMessage.STOP_WORKFLOW_SUCCESSFUL);

                        if (get(this.stopAction, 'redirectUrl')) {
                          this.onNavigationClick(this.stopAction.redirectUrl, formData);
                        }
                        else {
                          this.routerManagerService.reload();
                        }
                      } else if (savedData.code === 3 || savedData.code === 4) {
                        this.coreActionCreator.setLoader(false);
                        this.notificationMessageService.error(this.notificationMessage.ERROR_IN_STOP_WORKFLOW);
                      }
                    },
                    () => {
                      this.coreActionCreator.setLoader(false);
                      this.notificationMessageService.error(this.notificationMessage.ERROR_IN_STOP_WORKFLOW);
                    }
                  )
              );
              this.needToSave = false;
            }
          }
        }
      })
    );
  }

  private onNavigationClick(redirectUrl: string, formData: any) {
    const url = this.interpolateService.interpolate(redirectUrl, formData);
    const currentApplication = JSON.parse(sessionStorage.getItem('currentApplication'));
    const queryParameters = {'appname': currentApplication?.alias}
    this.route.navigate([url], {queryParams: queryParameters});
  }

  ngOnDestroy(): void {
    this.$subscription.unsubscribe();
    this.formId = '';
  }
}
